import PropTypes from 'prop-types';
import React, { useCallback, useRef, memo } from 'react';
import { Chevron, TrackableLink, createAccessibleOnClick } from '@surfline/quiver-react';
import { slugify, kbygPaths } from '@surfline/web-common';
import nearbySubregionsPropType from '../../propTypes/nearbySubregions';
import en from '../../intl/translations/en';
import subregionForecastChartsPath from '../../utils/urls/subregionForecastChartsPath';

const { subregionForecastPath, subregionPremiumAnalysisPath } = kbygPaths;

const linkTo = (subregionSlug, subregionId, subregionName, pathName) => {
  if (pathName.indexOf('surf-charts') !== -1) {
    return `${subregionForecastChartsPath(subregionId, 'wave-height', subregionName)}`;
  }
  if (pathName.indexOf('premium-analysis') !== -1) {
    return subregionPremiumAnalysisPath(subregionSlug, subregionId);
  }
  return subregionForecastPath(subregionSlug, subregionId);
};

const SubregionLink = memo(({ subregion, pathName, onClickLink }) => {
  const linkRef = useRef();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getEventProperties = useCallback(() => onClickLink(subregion), []);

  return (
    <TrackableLink
      ref={linkRef}
      eventName="Clicked Nearby Subregion"
      eventProperties={getEventProperties}
    >
      <a
        ref={linkRef}
        key={subregion._id}
        title={`${subregion.name} ${en.RegionalForecast.header.titleSuffix}`}
        className="sl-forecast-header-menu__nearby-subregion"
        href={linkTo(slugify(subregion.name), subregion._id, subregion.name, pathName)}
      >
        <span className="sl-forecast-header-menu__nearby-subregion__details__name">
          {subregion.name}
        </span>
      </a>
    </TrackableLink>
  );
});

const NearbySubregions = ({
  className,
  title,
  nearbySubregions,
  chevronDirection,
  pathName,
  onClickLink,
  onClick,
}) => (
  <div className={className} {...createAccessibleOnClick(onClick)}>
    <div className="sl-forecast-header-menu__dropdown__title">
      {title} <Chevron direction={chevronDirection} />
    </div>
    <div className="sl-forecast-header-menu__nearby-subregions">
      {nearbySubregions.map((subregion) => (
        <SubregionLink subregion={subregion} pathName={pathName} onClickLink={onClickLink} />
      ))}
    </div>
  </div>
);

SubregionLink.propTypes = {
  subregion: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    legacyId: PropTypes.number,
    offshoreSwellLocation: PropTypes.shape({
      type: PropTypes.string,
      coordinates: PropTypes.arrayOf(PropTypes.number),
    }),
  }).isRequired,
  pathName: PropTypes.string,
  onClickLink: PropTypes.func.isRequired,
};

NearbySubregions.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  nearbySubregions: nearbySubregionsPropType.isRequired,
  chevronDirection: PropTypes.oneOf(['down', 'up']).isRequired,
  pathName: PropTypes.string,
  onClickLink: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
};

SubregionLink.defaultProps = {
  pathName: null,
};

NearbySubregions.defaultProps = {
  className: null,
  pathName: null,
};

export default NearbySubregions;
